import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'src/api/axios';

/**
 * Hook koji povremeno poziva /api/my/ping da proveri da li je korisnik i dalje prijavljen
 * i da li su bile promene nad korisnikom
 * 
 * @param {number} interval - Interval u milisekundama (default: 15000 = 15 sekundi)
 */
export const usePing = (interval = 15000) => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const timeoutRef = useRef(null);
  const lastUpdatedAtRef = useRef(null);
  const isPingingRef = useRef(false);
  const isActiveRef = useRef(true);

  useEffect(() => {
    // Ako korisnik nije prijavljen, ne pokreći ping
    if (!auth.isLoggedIn || !auth.user) {
      // Očisti timeout ako postoji
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      lastUpdatedAtRef.current = null;
      isPingingRef.current = false;
      isActiveRef.current = false;
      return;
    }

    isActiveRef.current = true;

    // Postavi početni updated_at iz trenutnog korisnika
    if (auth.user && auth.user.updated_at && !lastUpdatedAtRef.current) {
      lastUpdatedAtRef.current = auth.user.updated_at;
    }

    const scheduleNextPing = () => {
      // Proveri da li je hook još aktivan
      if (!isActiveRef.current) {
        return;
      }

      // Očisti postojeći timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Postavi sledeći ping nakon intervala
      timeoutRef.current = setTimeout(() => {
        pingUser();
      }, interval);
    };

    const pingUser = async () => {
      // Proveri da li je hook još aktivan
      if (!isActiveRef.current) {
        return;
      }

      // Spreči višestruke simultane pozive
      if (isPingingRef.current) {
        // Ako je već u toku, zakazuj sledeći nakon intervala
        scheduleNextPing();
        return;
      }

      isPingingRef.current = true;

      try {
        const response = await axios.get('/api/my/ping', { 
          disableNotification: true 
        });

        if (response.data && response.data.data) {
          const newUser = response.data.data;
          // Koristi updated_at iz user objekta ili iz response.data
          const newUpdatedAt = newUser.updated_at || response.data.updated_at;

          // Proveri da li su bile promene
          if (lastUpdatedAtRef.current && newUpdatedAt && newUpdatedAt !== lastUpdatedAtRef.current) {
            // Ažuriraj korisnika u Redux store-u
            dispatch({ 
              type: 'UPDATE_USER', 
              payload: newUser 
            });
          } else if (!lastUpdatedAtRef.current) {
            // Prvi put - samo ažuriraj
            dispatch({ 
              type: 'UPDATE_USER', 
              payload: newUser 
            });
          }

          // Ažuriraj lastUpdatedAt
          if (newUpdatedAt) {
            lastUpdatedAtRef.current = newUpdatedAt;
          }
        }
      } catch (error) {
        // Ako je 401, korisnik više nije prijavljen
        if (error.response?.status === 401) {
          isActiveRef.current = false;
          dispatch({ type: 'LOGOUT' });
          // Ne prikazuj notifikaciju jer je ovo očekivano ponašanje
          return;
        }
        // Ostale greške ignorišemo (ne prikazujemo notifikacije)
      } finally {
        isPingingRef.current = false;
        
        // Zakazuj sledeći ping tek kada se trenutni završi
        if (isActiveRef.current) {
          scheduleNextPing();
        }
      }
    };

    // Očisti postojeći timeout pre nego što kreiraš novi
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Pozovi prvi ping odmah
    pingUser();

    // Cleanup funkcija
    return () => {
      isActiveRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      isPingingRef.current = false;
    };
  }, [auth.isLoggedIn, dispatch, interval]);
};

export default usePing;

